import { OctTable } from "@oceantech/oceantech-ui";
import {useContext, useEffect, useMemo, useState} from "react";
import { Button, InputGroup } from "react-bootstrap";
import DatePickerCustom from "../component/date-picker/DatePickerCustom";
import LabelRequired from "../component/LabelRequired";
import TextValidator from "../component/TextValidator";
import { IConfigTable } from "../models/OctResponse";
import {
  CODE_SUCCESS,
  INIT_SEARCH_OBJECT,
  TYPE,
  SEARCH_OBJECT_MAX_SIZE, BTN_ACTIONS,
} from "../utils/Constant";
import "./CorrectiveSubsection.scss";
import CorrectiveSubsectionModal from "./modals/CorrectiveSubsectionModal";
import { AppContext } from "../appContext/AppContext";
import {getCriteriaByPlan, getListCorrectivePlan, getPlanDetail} from "./services/CorrectiveSubsectionServices";
import { ISearchCorrectiveSubsection } from "./models/CorrectiveSubsectionModels";
import CorrectionColumns from "./columns";
import {CORRECTIVE_SUBSECTION_MODEL_JSON} from "./constants/commons";
import {CSProvider} from "./contexts";
import AutocompleteV2 from "../component/AutocompleteObjectV2";

const CorrectiveSubsection = () => {
  const { setIsLoading } = useContext(AppContext);
  const [objectSearch, setObjectSearch] = useState<ISearchCorrectiveSubsection>({
    ...INIT_SEARCH_OBJECT,
    pageSize: 20,
  });
  const [data, setData] = useState<any[]>([]);
  const [reload, setReload] = useState(false);
  const [itemDetail, setItemDetail] = useState<any>(null);
  const [isView, setIsView] = useState<boolean>(false);
  const [configTable, setConfigTable] = useState<IConfigTable>({
    totalPages: 1,
    totalElements: 5,
    numberOfElements: 5,
  });
  const [showModalCriteria, setShowModalCriteria] = useState<boolean>(false);
  const [dataChecked, setDataChecked] = useState<any[]>([]);

  useEffect(() => {
    handleGetListPlan().then(r => r);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, objectSearch.pageIndex, objectSearch.pageSize]);

  const handleGetListPlan = async () => {
    try {
      setIsLoading(true);
      const { data } = await getListCorrectivePlan({...objectSearch});
      if (data?.code === CODE_SUCCESS) {
        setData(data?.data?.content);
        setConfigTable({
          totalPages: data?.data?.totalPages,
          totalElements: data?.data?.totalElements,
          numberOfElements: data?.data?.numberOfElements,
        });
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCorrectionModal = (itemDetail: any, action: any) => {
    setIsView(action === BTN_ACTIONS.VIEW);
    handleGetDetailCriteia(itemDetail?.id).then(r => r);
  };

  const handleGetDetailCriteia = async (id: string) => {
    if (!id) return;
    try {
      setIsLoading(true);
      const { data } = await getPlanDetail(id);
      if (data?.code === CODE_SUCCESS) {
        setItemDetail(data?.data);
        setShowModalCriteria(true);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  }

  const correctionColumns = useMemo(() => CorrectionColumns({
    handleOpenCorrectionModal,
  }), []);

  const handleCloseCriteriaModal = () => {
    setShowModalCriteria(false);
    setReload(!reload);
    setItemDetail(null);
  }

  const handleChange = (name: string, value: any) => {
    setObjectSearch((prev: any) => ({
      ...objectSearch,
      [name]: value,
    }));
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      handleSubmitSearch();
    }
  };

  const handleSubmitSearch = () => {
    setReload(!reload);
    setObjectSearch({
      ...objectSearch,
      pageIndex: 1,
    });
  };

  const contextValue = useMemo(() => ({
    itemDetail,
    setItemDetail,
    isView,
    setIsView,
  }), [itemDetail, isView])

  return (
    <div className="pe-list">
      <CSProvider value={contextValue}>
        <div className="h-100 bg-white">
          <div className="reception-list__container spaces py-0">
            <div className="px-3 pt-2 border-bottom">
              <div className="text-title">Danh sách khắc phục</div>
            </div>

            <div className="d-flex justify-content-between align-items-center px-3 py-2 gap-2">
              <div className="d-flex spaces align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Từ ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="startDate"
                  value={objectSearch?.startDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("startDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="d-flex spaces align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Đến ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="endDate"
                  value={objectSearch?.endDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("endDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="text-lable-input mb-0 spaces spaces mt-5 mr-5 form-label max-content-width">Từ khóa</div>
              <div className="flex-grow-1">
                <InputGroup className="spaces h-29 px-0">
                  <TextValidator
                    className="spaces h-29 min-w-200 rounded-end-0"
                    name="keyword"
                    placeholder="Tìm kiếm theo mã, tên kế hoạch"
                    value={objectSearch?.keyword || ""}
                    onChange={(e: any) =>
                      handleChange("keyword", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                  />
                  <Button
                    className="btn-fill spaces h-29"
                    onClick={handleSubmitSearch}
                  >
                    <i className="bi bi-search"></i>
                    <span>Lọc</span>
                  </Button>
                </InputGroup>
              </div>
            </div>

            <OctTable
              id="searchCriteria"
              className={`mt-7 table-background-th width-100 spaces h-calc-vh-230`}
              data={data}
              columns={correctionColumns}
              searchObject={objectSearch}
              setSearchObject={setObjectSearch}
              fixedColumnsCount={-1}
              noToolbar={true}
              totalPages={configTable.totalPages}
              totalElements={configTable.totalElements}
              numberOfElements={configTable.numberOfElements}
              // handleDoubleClick={handleDoubleClick}
              isShowSelection={false}
              clearToolbar={true}
              type={TYPE.MULTILINE}
              setDataChecked={(dataChecked) => setDataChecked(dataChecked)}
            />
          </div>
        </div>

        {showModalCriteria && (
          <CorrectiveSubsectionModal
            show={showModalCriteria}
            handleCloseDialog={handleCloseCriteriaModal}
            type={CORRECTIVE_SUBSECTION_MODEL_JSON.CORRECTION_FORM}
            itemDetail={itemDetail}
            setItemDetail={setItemDetail}
          />
        )}
      </CSProvider>
    </div>
  );
};

export default CorrectiveSubsection;
